/**
 * Main App Component for FlowRide Frontend
 * This is the root component that sets up the application structure,
 * routing, theming, and context providers for the entire application.
 */

import { IonApp, IonRouterOutlet, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, Route, useLocation } from "react-router-dom";
import Home from "./pages/Home/Home";
import Questionnaire from "./pages/StarterQuestions/Quiz";

import type { } from "@mui/x-date-pickers/themeAugmentation";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/* Theme variables */
import "./theme/variables.css";

// Swipper styling
import "swiper/css";
import "swiper/css/effect-cards";

// General app styling
import "./style.css";

import { createTheme, ThemeProvider } from "@mui/material";
import { frFR } from "@mui/material/locale";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/fr-ch";
import { AuthBootstrap } from "./components/AuthBootstrap";
import { ConsoleInterceptorTest } from "./components/ConsoleInterceptorTest";
import ErrorBoundary from "./components/Errors/ErrorBoundary";
import { PayrexxNativeReturnBootstrap } from "./components/PayrexxNativeReturnBootstrap";
import { PurchasedItinerariesBootstrap } from "./components/PurchasedItinerariesBootstrap";
import { SavedItinerariesBootstrap } from "./components/SavedItinerariesBootstrap";
import { ServicesInitializer } from "./components/ServicesInitializer";
import { SessionRestoreBootstrap } from "./components/SessionRestore/SessionRestoreBootstrap";
import { StopLinesBootstrap } from "./components/StopLinesBootstrap";
import { SwissPassLoginOfferPrompt } from "./components/SwissPassLoginOfferPrompt";
import { TicketPurchaseModal } from "./components/TicketPurchase/TicketPurchaseModal";
import { TicketViewerModal } from "./components/TicketViewer/TicketViewerModal";
import { APP_DIAGNOSTICS, TICKET_PAYMENT } from "./config/global.configuration";
import SwissPassReturnPage from "./pages/Auth/SwissPassReturnPage";
import PayrexxReturnPage from "./pages/Payment/PayrexxReturnPage";
import {
    ephemeralStore,
    offerPurchaseLoginPromptSlice,
    PricingBootstrap,
    TICKET_VIEWER_DEFAULT,
    ticketPurchaseSlice,
    ticketViewerSlice,
    useEphemeralSelector,
} from "./store/ephemeral";
import {
    authSlice,
    mapPrefsSlice,
    PersistenceProvider,
    usePersistedSlice,
    usePersistedSliceSelector,
} from "./store/persistence";
import { getUIColorPalette } from "./theme/ColorPalette";
import { logAppMemoryReport } from "./tools/AppMemoryProfiler.tools";

setupIonicReact();

/** SwissPass OAuth return path prefix. */
const SWISSPASS_PATH_PREFIX = "/login/swisspass";

/**
 * Ionic's stack router can fail to mount deep-link OAuth returns on cold load.
 * Render all SwissPass return paths outside {@link IonRouterOutlet};
 * non-callback suffixes are redirected to /home.
 */
const AppRoutes: React.FC<{ "ClientQuestionnaire": React.ComponentType }> = ( { ClientQuestionnaire } ) => {
    const location = useLocation();
    const isSwissPassRoute =
        location.pathname === SWISSPASS_PATH_PREFIX ||
        location.pathname.startsWith( `${ SWISSPASS_PATH_PREFIX }/` );

    /*
     * The Payrexx return page also mounts outside IonRouterOutlet: the browser
     * lands here after a full-page redirect and must not depend on the stack router.
     */
    const isPayrexxReturnRoute = location.pathname === TICKET_PAYMENT.webReturnPath;

    if ( isSwissPassRoute ) {
        return <SwissPassReturnPage />;
    }

    if ( isPayrexxReturnRoute ) {
        return <PayrexxReturnPage />;
    }

    return (
        <IonRouterOutlet>
            <Route
                component={Home}
                exact
                path="/home"
            />
            <Route
                component={ClientQuestionnaire}
                exact
                path="/questions"
            />
            <Route
                component={ConsoleInterceptorTest}
                exact
                path="/test-console"
            />
            <Route
                exact
                path="/"
                render={() => <Redirect to="/home" />}
            />
        </IonRouterOutlet>
    );
};

/**
 * Higher-order component for client authentication
 * Wraps components that require user authentication
 * Redirects to login page if user is not authenticated
 * @param Component - React component to protect
 * @returns Protected component that checks authentication
 */
function requireClient( Component: React.ComponentType ) {
    return function ProtectedComponent( props: Record<string, unknown> ) {
        const [ auth ] = usePersistedSlice( authSlice );

        if ( auth.accessToken == null || auth.accessToken === undefined ) {
            return <Redirect to="/home" />;
        }

        return <Component {...props} />;
    };
}

/**
 * Main App Component
 * Sets up the application with routing, theming, and context providers
 * Manages service worker updates and authentication
 */
const App: React.FC = () => {
    return (
        <PersistenceProvider>
            <PricingBootstrap />
            <StopLinesBootstrap />
            <AuthBootstrap />
            <PurchasedItinerariesBootstrap />
            <ServicesInitializer>
                <AppContent />
            </ServicesInitializer>
        </PersistenceProvider>
    );
};

const AppContent: React.FC = () => {
    const mapAppearanceMode = usePersistedSliceSelector(
        mapPrefsSlice,
        ( mapPrefs ) => mapPrefs.appearanceMode,
    );
    const [ systemPrefersDark, setSystemPrefersDark ] = useState<boolean>( () => {
        if ( typeof window === "undefined" ) {
            return false;
        }

        return window.matchMedia( "(prefers-color-scheme: dark)" ).matches;
    } );

    useEffect( () => {
        const mediaQuery = window.matchMedia( "(prefers-color-scheme: dark)" );
        const handleSystemThemeChange = ( event: MediaQueryListEvent ) => {
            setSystemPrefersDark( event.matches );
        };

        mediaQuery.addEventListener( "change", handleSystemThemeChange );

        return () => {
            mediaQuery.removeEventListener( "change", handleSystemThemeChange );
        };
    }, [] );

    const isDarkUi = useMemo( () => {
        if ( mapAppearanceMode === "auto" ) {
            return systemPrefersDark;
        }

        return mapAppearanceMode === "dark";
    }, [ mapAppearanceMode, systemPrefersDark ] );
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    useEffect( () => {
        const intervalMs = APP_DIAGNOSTICS.memoryProfilingPeriodicIntervalMs;
        if ( intervalMs <= 0 || !APP_DIAGNOSTICS.memoryProfilingEnabled ) {
            return;
        }

        const intervalId = setInterval( () => {
            logAppMemoryReport( {
                "context":          "periodic",
                "includeIndexedDb": !import.meta.env.DEV,
            } );
        }, intervalMs );

        return () => {
            clearInterval( intervalId );
        };
    }, [] );

    const theme = useMemo( () => {
        return createTheme(
            {
                "palette": {
                    "mode":       isDarkUi ? "dark" : "light",
                    "primary":    { "main": palette.brandPrimary },
                    "background": {
                        "default": palette.appPageBackground,
                        "paper":   palette.modalCardBackground,
                    },
                    "text": {
                        "primary":   palette.overlayTextPrimary,
                        "secondary": palette.overlayTextSecondary,
                    },
                },
                "components": {
                    "MuiClock": {
                        "styleOverrides": {
                            "root": {
                                "& .MuiClock-pin":          { "backgroundColor": palette.brandPrimary },
                                "& .MuiClockPointer-root":  { "backgroundColor": palette.brandPrimary },
                                "& .MuiClockPointer-thumb": {
                                    "border":          `16px solid ${ palette.brandPrimary }`,
                                    "backgroundColor": palette.brandPrimary,
                                },
                            },
                        },
                    },
                    "MuiPickersToolbar": {
                        "styleOverrides": {
                            "root": {
                                "backgroundColor": palette.brandPrimary,
                                "color":           palette.textOnBrandPrimary,
                            },
                        },
                    },
                    "MuiButton":   { "styleOverrides": { "root": { "&.MuiButtonBase-root": { "color": palette.brandPrimary } } } },
                    "MuiMenuItem": {
                        "styleOverrides": {
                            "root": {
                                "&.MuiMultiSectionDigitalClockSection-item.Mui-selected": {
                                    "backgroundColor": palette.brandPrimary,
                                    "color":           palette.textOnBrandPrimary,
                                },
                            },
                        },
                    },
                    "MuiOutlinedInput": {
                        "styleOverrides": {
                            "root": {
                                "borderRadius":                                   "0.75rem",
                                "borderColor":                                    palette.brandPrimary,
                                "height":                                         "42px",
                                "marginRight":                                    "10px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { "borderColor": palette.brandPrimary },
                                "& .MuiInputBase-input":                          {
                                    "textAlign": "center",
                                    "padding":   "14px 0",
                                },
                            },
                        },
                    },
                },
            },
            frFR,
        );
    }, [ isDarkUi, palette ] );

    /** Protected questionnaire component that requires authentication */
    const ClientQuestionnaire = requireClient( Questionnaire );

    const isOfferPurchaseLoginPromptOpen = useEphemeralSelector(
        offerPurchaseLoginPromptSlice,
        ( state ) => state.isOpen,
    );
    const closeOfferPurchaseLoginPrompt = useCallback( (): void => {
        ephemeralStore.setValue( offerPurchaseLoginPromptSlice, { "isOpen": false } );
    }, [] );

    const ticketPurchaseState = useEphemeralSelector( ticketPurchaseSlice, ( state ) => state );
    const closeTicketPurchaseModal = useCallback( (): void => {
        ephemeralStore.setValue( ticketPurchaseSlice, ( prev ) => ( {
            ...prev,
            "isOpen": false,
        } ) );
    }, [] );

    const ticketViewerState = useEphemeralSelector( ticketViewerSlice, ( state ) => state );
    const closeTicketViewerModal = useCallback( (): void => {
        ephemeralStore.setValue( ticketViewerSlice, TICKET_VIEWER_DEFAULT );
    }, [] );

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider
                adapterLocale="fr-ch"
                dateAdapter={AdapterDayjs}
                localeText={{ "cancelButtonLabel": "Annuler" }}
            >
                <IonApp>
                    <IonReactRouter>
                        <ErrorBoundary>
                            <AppRoutes ClientQuestionnaire={ClientQuestionnaire} />
                            <SessionRestoreBootstrap isDarkUi={isDarkUi} />
                            <SavedItinerariesBootstrap isDarkUi={isDarkUi} />
                            <PayrexxNativeReturnBootstrap />
                            <SwissPassLoginOfferPrompt
                                isDarkUi={isDarkUi}
                                isOpen={isOfferPurchaseLoginPromptOpen}
                                onClose={closeOfferPurchaseLoginPrompt}
                            />
                            <TicketPurchaseModal
                                isDarkUi={isDarkUi}
                                isOpen={ticketPurchaseState.isOpen}
                                itinerary={ticketPurchaseState.itinerary}
                                onClose={closeTicketPurchaseModal}
                            />
                            <TicketViewerModal
                                initialSegmentId={ticketViewerState.initialSegmentId}
                                isDarkUi={isDarkUi}
                                isOpen={ticketViewerState.isOpen}
                                itineraryId={ticketViewerState.itineraryId}
                                onClose={closeTicketViewerModal}
                            />
                        </ErrorBoundary>
                    </IonReactRouter>
                </IonApp>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default App;

